import { Search } from "lucide-react";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { DateRangePicker } from "./date-range-picker";

interface ListHeaderProps {
  readonly title: string | React.ReactNode;
  readonly searchValue: string;
  readonly onSearchChange: (value: string) => void;
  readonly onDateRangeChange?: (
    startDate: Date | undefined,
    endDate: Date | undefined
  ) => void;
  readonly rightAction?: React.ReactNode;
  readonly showDateFilter?: boolean;
  readonly showSearchCount?: number;
  readonly leftAction?: React.ReactNode;
  readonly titleClassName?: string;
}

export default function ListHeader({
  title,
  searchValue,
  onSearchChange,
  onDateRangeChange,
  rightAction,
  showDateFilter = true,
  showSearchCount = 0,
  leftAction,
  titleClassName,
}: ListHeaderProps) {
  const [startDate, setStartDate] = React.useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = React.useState<Date | undefined>(undefined);

  // Handle date range changes
  const handleDateRangeChange = (
    dates: [Date | undefined, Date | undefined]
  ) => {
    const [newStartDate, newEndDate] = dates;
    setStartDate(newStartDate);
    setEndDate(newEndDate);

    if (onDateRangeChange) {
      onDateRangeChange(newStartDate, newEndDate);
    }
  };
  const [localSearchValue, setLocalSearchValue] = useState(searchValue);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Update local value when prop changes (for controlled component behavior)
  useEffect(() => {
    setLocalSearchValue(searchValue);
  }, [searchValue]);

  // Handle search input with debounce
  const handleSearchChange = (value: string) => {
    setLocalSearchValue(value);

    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set a new timer
    debounceTimerRef.current = setTimeout(() => {
      onSearchChange(value);
    }, 500); // 500ms debounce delay
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="w-full flex-shrink-0 py-4 md:py-[24px]">
      {/*
        One flex-wrap row. Controls keep fixed widths (shrink-0) and wrap as
        whole units when space runs out — never compress into the actions.
        leftAction uses `contents` so its children join this same wrap flow.
      */}
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <h2
          className={`shrink-0 text-[20px] font-bold leading-[48px] text-[#1F1F1F] ${
            titleClassName || ""
          }`}
        >
          {title}{" "}
          {showSearchCount
            ? `(${String(showSearchCount).padStart(2, "0")})`
            : ""}
        </h2>

        <div className="relative h-[48px] w-full shrink-0 sm:w-[220px]">
          <input
            type="text"
            placeholder="Search"
            value={localSearchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="h-full w-full rounded-[12px] border border-[#E5E7EB] py-[14px] pl-[24px] pr-[56px] text-[14px] font-medium text-[#1F1F1F] placeholder:text-[#C0C0C0] focus:outline-none"
          />
          <div className="absolute right-[24px] top-[7px] flex h-[32px] w-[32px] items-center justify-center rounded-[100px] bg-[#f2f2f2] p-[4px]">
            <Search className="h-[20px] w-[20px]" />
          </div>
        </div>

        {showDateFilter ? (
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            maxDate={new Date()}
            onChange={handleDateRangeChange}
            placeholder="Select Date"
            className="h-[48px] w-full shrink-0 sm:w-[294px]"
          />
        ) : null}

        {leftAction ? <div className="contents">{leftAction}</div> : null}

        {rightAction ? (
          <div className="ml-auto flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:justify-end">
            {rightAction}
          </div>
        ) : null}
      </div>
    </div>
  );
}
